import React, { useState, useEffect, useRef } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  ClickAwayListener,
  Chip,
  Snackbar,
  Alert,
} from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SearchIcon from '@mui/icons-material/Search';
import PlaceIcon from '@mui/icons-material/Place';
import CloseIcon from '@mui/icons-material/Close';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';
import BookMyOpAPI from '../services/BookMyOpapi';
import { searchAddressPredictions, getPlaceDetails, reverseGeocode } from '../services/googleMapsService';
import storage from '../utils/storage';

const LocationSelector = ({ 
  onLocationUpdate,
  placeholder = "Search location...",
  sx = {},
  fullWidth = false,
  showCurrentLocation = true,
}) => {
  const theme = useTheme();
  const { isAuthenticated, userLocation, refreshProfile } = useBookMyOpAuth();
  
  const [currentLocation, setCurrentLocation] = useState('');
  const [locationData, setLocationData] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Address search state
  const [addressSearch, setAddressSearch] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const searchTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  // Load location from user profile or session storage on mount
  useEffect(() => {
    // First, try to get location from userLocation context
    if (userLocation && (userLocation.city || userLocation.latitude)) {
      setLocationData(userLocation);
      const locationText = userLocation.city 
        ? `${userLocation.city}${userLocation.state ? ', ' + userLocation.state : ''}`
        : '';
      setCurrentLocation(locationText);
      return;
    }

    // If authenticated, check storage
    if (isAuthenticated) {
      const userDataStr = storage.getItemSync('bookmyop_user_data');
      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr);
          const city = userData.city || userData.current_city;
          const state = userData.state || userData.current_state;
          if (userData.latitude || userData.longitude || city) {
            const locationInfo = {
              latitude: userData.latitude,
              longitude: userData.longitude,
              city: city,
              state: state,
              pincode: userData.pincode,
              country: userData.country
            };
            setLocationData(locationInfo);
            const locationText = city 
              ? `${city}${state ? ', ' + state : ''}`
              : '';
            setCurrentLocation(locationText);
            return;
          }
        } catch (error) {
          console.error('Error parsing bookmyop_user_data:', error);
        }
      }
      
      // Async check for mobile
      storage.getItem('bookmyop_user_data').then(userDataStr => {
        if (userDataStr) {
          try {
            const userData = JSON.parse(userDataStr);
            const city = userData.city || userData.current_city;
            const state = userData.state || userData.current_state;
            
            if (userData.latitude || userData.longitude || city) {
              const locationInfo = {
                latitude: userData.latitude,
                longitude: userData.longitude,
                city: city,
                state: state,
                pincode: userData.pincode,
                country: userData.country
              };
              setLocationData(locationInfo);
              const locationText = city 
                ? `${city}${state ? ', ' + state : ''}`
                : '';
              setCurrentLocation(locationText);
            }
          } catch (error) {
            console.error('Error parsing bookmyop_user_data (async):', error);
          }
        }
      }).catch(err => {
        console.error('Error reading from Capacitor Preferences:', err);
      });
    }

    // For guest users - check session storage
    const sessionLocation = sessionStorage.getItem('guest_location_data');
    if (sessionLocation) {
      try {
        const locationData = JSON.parse(sessionLocation);
        setLocationData(locationData);
        const locationText = locationData.city 
          ? `${locationData.city}${locationData.state ? ', ' + locationData.state : ''}`
          : '';
        setCurrentLocation(locationText);
      } catch (error) {
        console.error('Error parsing session location:', error);
        setCurrentLocation('');
        setLocationData(null);
      }
    } else {
      setCurrentLocation('');
      setLocationData(null);
    }
  }, [userLocation, isAuthenticated]);

  // Listen for location updates from other components
  useEffect(() => {
    const handleLocationUpdate = (event) => {
      const { locationData: newLocationData } = event.detail;
      if (newLocationData) {
        setLocationData(newLocationData);
        const locationText = newLocationData.city 
          ? `${newLocationData.city}${newLocationData.state ? ', ' + newLocationData.state : ''}`
          : '';
        setCurrentLocation(locationText);
      }
    };

    window.addEventListener('locationUpdated', handleLocationUpdate);
    return () => window.removeEventListener('locationUpdated', handleLocationUpdate);
  }, []);

  // Update suggestions visibility
  useEffect(() => {
    if (addressSearch.length >= 3 && addressSuggestions.length > 0) {
      setOpen(true);
    } else if (addressSearch.length < 3) {
      setOpen(false);
    }
  }, [addressSearch, addressSuggestions]);

  // Address search handlers
  const handleAddressSearchChange = (event) => {
    const query = event.target.value;
    setAddressSearch(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.trim().length < 3) {
      setAddressSuggestions([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchAddress(query);
    }, 500);
  };

  const searchAddress = async (query) => {
    setIsSearchingAddress(true);
    try {
      const predictions = await searchAddressPredictions(query);
      setAddressSuggestions(predictions);
    } catch (error) {
      console.error('Address search error:', error);
      if (error.message.includes('API key not configured')) {
        setSnackbar({
          open: true,
          message: 'Google Maps API key not configured',
          severity: 'error'
        });
      }
      setAddressSuggestions([]);
    } finally {
      setIsSearchingAddress(false);
    }
  };

  const handleAddressSelect = async (suggestion) => {
    setAddressSearch('');
    setAddressSuggestions([]);
    setOpen(false);

    try {
      const placeDetails = await getPlaceDetails(suggestion.place_id);
      
      let response;
      if (isAuthenticated) {
        const locationPayload = {
          latitude: placeDetails.latitude,
          longitude: placeDetails.longitude,
          address: placeDetails.address,
          city: placeDetails.city,
          state: placeDetails.state,
          pincode: placeDetails.pincode
        };
        response = await BookMyOpAPI.updateLocation(locationPayload);
        
        try {
          await BookMyOpAPI.setSessionLocation(locationPayload);
          sessionStorage.setItem('session_location_set', 'true');
        } catch (sessionError) {
          console.warn('Failed to set session location:', sessionError);
        }
      } else {
        response = await BookMyOpAPI.setSessionLocation({
          latitude: placeDetails.latitude,
          longitude: placeDetails.longitude,
          address: placeDetails.address,
          city: placeDetails.city,
          state: placeDetails.state,
          pincode: placeDetails.pincode
        });
        sessionStorage.setItem('session_location_set', 'true');
      }

      if (response.status === 'success' || response.data) {
        const data = response.data || response;
        
        const mergedLocationData = {
          ...data,
          city: data.city || placeDetails.city || null,
          state: data.state || placeDetails.state || null,
          pincode: data.pincode || placeDetails.pincode || null,
        };
        
        setLocationData(mergedLocationData);
        
        const locationText = placeDetails.city 
          ? `${placeDetails.city}${placeDetails.state ? ', ' + placeDetails.state : ''}`
          : data.city 
          ? `${data.city}${data.state ? ', ' + data.state : ''}`
          : data.current_city
          ? `${data.current_city}${data.current_state ? ', ' + data.current_state : ''}`
          : '';
        
        setCurrentLocation(locationText);
        
        if (!isAuthenticated) {
          sessionStorage.setItem('guest_location_data', JSON.stringify(data));
        }
        
        if (isAuthenticated) {
          try {
            await refreshProfile(true);
          } catch (error) {
            console.error('Profile refresh failed, but location was updated:', error);
          }
        }
        
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('locationUpdated', { 
          detail: { locationData: data } 
        }));

        // Call callback if provided
        if (onLocationUpdate) {
          onLocationUpdate(data);
        }

        // For guest users, reload the page to refresh all location-based results
        if (!isAuthenticated) {
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      }
    } catch (error) {
      console.error('Failed to save location:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update location. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleClearSearch = () => {
    setAddressSearch('');
    setAddressSuggestions([]);
    setOpen(false);
  };

  const handleFocus = () => {
    if (inputRef.current) {
      setAnchorEl(inputRef.current);
    }
    if (addressSearch.length >= 3 && addressSuggestions.length > 0) {
      setOpen(true);
    }
  };

  const handleBlur = () => {
    // Delay closing to allow clicking on suggestions
    setTimeout(() => setOpen(false), 200);
  };

  return (
    <>
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Box sx={{ position: 'relative', width: fullWidth ? '100%' : 'auto', ...sx }}>
        <TextField
          inputRef={inputRef}
          fullWidth={fullWidth}
          placeholder={placeholder}
          value={addressSearch}
          onChange={handleAddressSearchChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          size='small'
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ gap: 1, mr: 1 }}>
                {isSearchingAddress && (
                  <CircularProgress size={20} sx={{ color: 'text.secondary' }} />
                )}
                {addressSearch && !isSearchingAddress && (
                  <IconButton size="small" onClick={handleClearSearch} sx={{ p: 0.5 }}>
                    <CloseIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                )}
                {showCurrentLocation && currentLocation && !addressSearch && !isSearchingAddress && (
                  <Chip
                    icon={<LocationOnOutlinedIcon sx={{ fontSize: '0.7rem !important' }} />}
                    label={currentLocation}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '0px  !important',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      '& .MuiChip-icon': {
                        color: theme.palette.primary.main,
                      }
                    }}
                  />
                )}
                {!addressSearch && !isSearchingAddress && (!showCurrentLocation || !currentLocation) && (
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                )}
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              padding: '0px 10px 0px 10px !important',
              borderRadius: '12px',
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
              },
            },
            '& .MuiInputBase-input': {
              padding: '0px !important',
            }
          }}
        />

        {/* Suggestions Dropdown */}
        <Popper
          open={open && (addressSuggestions.length > 0 || isSearchingAddress)}
          anchorEl={anchorEl}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorEl?.offsetWidth }}
        >
          <Paper
            elevation={8}
            sx={{
              mt: 1,
              borderRadius: '12px',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            {isSearchingAddress && (
              <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {!isSearchingAddress && addressSuggestions.length > 0 && (
              <List sx={{ py: 1 }}>
                {addressSuggestions.map((suggestion, index) => (
                  <ListItem key={suggestion.place_id || index} disablePadding>
                    <ListItemButton
                      onClick={() => handleAddressSelect(suggestion)}
                      sx={{
                        borderRadius: '8px',
                        mx: 1,
                        mb: 0.5,
                        '&:hover': {
                          background: alpha(theme.palette.primary.main, 0.08),
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <PlaceIcon sx={{ color: theme.palette.primary.main, fontSize: '1.2rem' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {suggestion.structured_formatting?.main_text || suggestion.description}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary" sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}>
                            {suggestion.structured_formatting?.secondary_text || suggestion.description}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}

            {!isSearchingAddress && addressSearch.length >= 3 && addressSuggestions.length === 0 && (
              <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No locations found. Try a different search.
                </Typography>
              </Box>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>

    {/* Snackbar for notifications */}
    <Snackbar
      open={snackbar.open}
      autoHideDuration={4000}
      onClose={() => setSnackbar({ ...snackbar, open: false })}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        severity={snackbar.severity}
        variant="filled"
        sx={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  </>
  );
};

export default LocationSelector;

